import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FiX as X, FiLock as Lock, FiCheckCircle as CheckCircle } from 'react-icons/fi';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyName?: string | null;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, companyName }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  if (!isOpen) return null;

  // Clear anonymous session before navigating to login/signup,
  // otherwise App.js route guard sees an authenticated user and
  // redirects away from /login back to /hub/dashboard.
  const clearAnonymousSession = async (): Promise<void> => {
    localStorage.removeItem('anonymous_session');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    await logout();
  };

  const handleLogin = async () => {
    await clearAnonymousSession();
    navigate('/login', { replace: true });
  };

  const handleSignup = async () => {
    await clearAnonymousSession();
    navigate('/signup', { replace: true });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-xl w-full transition-colors">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
              <Lock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Unlock Full Access</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Company Context */}
          {companyName && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                You&apos;re currently viewing assessment data for <strong>{companyName}</strong> as a guest.
              </p>
            </div>
          )}

          {/* Message */}
          <div>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              You&apos;re in guest mode with limited access. Create an account or log in to:
            </p>

            {/* Benefits List */}
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">
                  Access all CloudOps, DataOps, and GenAIOps features
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">View real project data and analytics</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">
                  Save and track your organization&apos;s progress
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">Collaborate with your team members</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col space-y-3 pt-4">
            <button
              type="button"
              onClick={handleSignup}
              className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md"
            >
              Create Account
            </button>
            <button
              type="button"
              onClick={handleLogin}
              className="w-full px-6 py-3 border-2 border-blue-600 text-blue-600 dark:text-blue-400 font-semibold rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Log In
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors text-sm"
            >
              Continue as Guest
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
